import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

/**
 * Touch/mouse/pen signature pad. Renders a canvas the user can draw on.
 *
 * Exposed via ref (useImperativeHandle):
 *   - getDataUrl(): string | null — base64 PNG data URL of the drawn signature
 *   - isEmpty(): boolean — true when nothing was drawn
 *   - clear(): void — wipes the canvas
 */
export const SignaturePad = forwardRef(function SignaturePad(
  { width = 640, height = 260, className = "", onChange },
  ref
) {
  const canvasRef = useRef(null);
  const emptyRef = useRef(true);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.lineWidth = 2.6;
    ctx.strokeStyle = "#0f172a";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);

    let drawing = false;
    let last = null;

    const pointer = (e) => {
      const rect = canvas.getBoundingClientRect();
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      return { x: cx - rect.left, y: cy - rect.top };
    };

    const start = (e) => {
      e.preventDefault();
      drawing = true;
      last = pointer(e);
      // dot on single tap
      ctx.beginPath();
      ctx.arc(last.x, last.y, 1.2, 0, Math.PI * 2);
      ctx.fillStyle = "#0f172a";
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      emptyRef.current = false;
      setEmpty(false);
      onChange?.();
    };
    const move = (e) => {
      if (!drawing) return;
      e.preventDefault();
      const p = pointer(e);
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      last = p;
    };
    const end = (e) => {
      if (drawing) e.preventDefault();
      drawing = false;
      last = null;
    };

    canvas.addEventListener("pointerdown", start);
    canvas.addEventListener("pointermove", move);
    canvas.addEventListener("pointerup", end);
    canvas.addEventListener("pointercancel", end);
    canvas.addEventListener("pointerleave", end);
    canvas.addEventListener("touchstart", start, { passive: false });
    canvas.addEventListener("touchmove", move, { passive: false });
    canvas.addEventListener("touchend", end);
    return () => {
      canvas.removeEventListener("pointerdown", start);
      canvas.removeEventListener("pointermove", move);
      canvas.removeEventListener("pointerup", end);
      canvas.removeEventListener("pointercancel", end);
      canvas.removeEventListener("pointerleave", end);
      canvas.removeEventListener("touchstart", start);
      canvas.removeEventListener("touchmove", move);
      canvas.removeEventListener("touchend", end);
    };
  }, [width, height, onChange]);

  useImperativeHandle(ref, () => ({
    getDataUrl: () => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      return canvas.toDataURL("image/png");
    },
    isEmpty: () => emptyRef.current,
    clear: () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.restore();
      emptyRef.current = true;
      setEmpty(true);
      onChange?.();
    },
  }));

  return (
    <div className={className}>
      <canvas
        ref={canvasRef}
        className="border border-dashed border-gold-deep/40 rounded-md bg-white touch-none select-none w-full max-w-full"
        data-testid="signature-canvas"
        style={{ touchAction: "none" }}
      />
      {empty && (
        <p className="text-xs text-muted-foreground mt-1 italic">Semnează cu degetul sau stylus-ul direct pe tabletă</p>
      )}
    </div>
  );
});

export default SignaturePad;
